/** @jsxImportSource solid-js */
import { type PinPosition } from "@previewloop/shared";
import { createEffect, createSignal, For, onCleanup, onMount, Show, type JSX } from "solid-js";
import { useWidgetStore } from "../hooks/widget-context";
import { type CollectedEntry } from "../store/widget-store";
import { autosizeTextarea } from "../utils/autosize-textarea";
import { createPresence } from "./create-presence";
import { WidgetBubble } from "./widget-bubble";
import { CARD_TEXTAREA_CLASS, WidgetBubbleGrip } from "./widget-bubble-grip";
import { WidgetComposer } from "./widget-composer";
import { WidgetIcon } from "./widget-icon";
import { WidgetLinkedTargets } from "./widget-linked-targets";

const PIN_CLASS =
  "z-pl bg-pl-gradient text-pl-primary-foreground shadow-pl fixed grid h-[26px] w-[26px] -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border-2 border-white font-sans text-xs font-semibold";

const HOVER_CLOSE_DELAY = 220;

const canHover = (): boolean =>
  typeof matchMedia === "function" && matchMedia("(hover: hover) and (pointer: fine)").matches;

export function WidgetPins(): JSX.Element {
  const store = useWidgetStore();

  // Pins were captured at page coordinates; track scroll so they stay glued to
  // their element instead of floating with the viewport.
  const [scroll, setScroll] = createSignal({ x: window.scrollX, y: window.scrollY });
  onMount(() => {
    let queued = false;
    const onScroll = (): void => {
      // Nothing scroll-anchored on screen: skip the signal churn entirely.
      if (store.collected().length === 0 && !store.composer()) return;
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        setScroll({ x: window.scrollX, y: window.scrollY });
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onCleanup(() => window.removeEventListener("scroll", onScroll));
  });
  // The skip above lets the signal go stale while nothing is on screen; the
  // moment something scroll-anchored appears it must resync or the first pin
  // renders offset by whatever was scrolled in the meantime.
  createEffect(() => {
    if (store.collected().length > 0 || store.composer()) {
      setScroll({ x: window.scrollX, y: window.scrollY });
    }
  });

  const pinX = (pin: PinPosition): number => pin.x + pin.scrollX - scroll().x;
  const pinY = (pin: PinPosition): number => pin.y + pin.scrollY - scroll().y;

  let hoverCloseTimer: ReturnType<typeof setTimeout> | undefined;
  const cancelHoverClose = (): void => {
    if (hoverCloseTimer) clearTimeout(hoverCloseTimer);
    hoverCloseTimer = undefined;
  };
  const scheduleHoverClose = (id: string): void => {
    cancelHoverClose();
    hoverCloseTimer = setTimeout(() => store.closePinHover(id), HOVER_CLOSE_DELAY);
  };
  onCleanup(cancelHoverClose);

  const activeEntry = (): CollectedEntry | null => {
    const active = store.activePin();
    if (!active) return null;
    return store.collected().find((entry) => entry.id === active.id) ?? null;
  };
  const bubblePresence = createPresence(activeEntry);
  const composerPresence = createPresence(store.composer);
  const [expandedTargetsEntryId, setExpandedTargetsEntryId] = createSignal<string | null>(null);

  // Ghost the active entry's saved strokes back onto the page (the draw
  // layer tracks scrolling itself).
  createEffect(() => {
    const active = store.activePin();
    store.previewEntryStrokes(active ? active.id : null);
  });
  onCleanup(() => store.previewEntryStrokes(null));

  // Grip handlers surfaced by the bubble shells; the cards render the grips.
  let composerDragStart: ((e: PointerEvent) => void) | undefined;
  let editorDragStart: ((e: PointerEvent) => void) | undefined;

  return (
    <>
      <For each={store.collected()}>
        {(entry, index) => (
          <button
            type="button"
            data-pl-pin=""
            aria-label={`Feedback ${index() + 1}`}
            class={`${PIN_CLASS} cursor-pointer border-0 p-0 transition-transform duration-[130ms] ease-[cubic-bezier(0.23,1,0.32,1)] before:absolute before:-inset-2 before:content-[''] active:scale-95 [@media(hover:hover)_and_(pointer:fine)]:hover:scale-110`}
            style={{
              left: `${pinX(entry.pin)}px`,
              top: `${pinY(entry.pin)}px`,
            }}
            onMouseEnter={() => {
              if (!canHover()) return;
              cancelHoverClose();
              store.openPinHover(entry.id);
            }}
            onMouseLeave={() => {
              if (!canHover()) return;
              scheduleHoverClose(entry.id);
            }}
            onClick={() => store.togglePinSticky(entry.id)}
          >
            {index() + 1}
          </button>
        )}
      </For>

      <Show when={composerPresence.current()} keyed>
        {(session) => {
          const targets = () => store.composerTargets() ?? session.initialTargets;
          const pin = () => targets()[0].pin;
          return (
            <>
              <div
                data-pl-pin=""
                class={`${PIN_CLASS} pointer-events-none`}
                style={{
                  left: `${pinX(pin())}px`,
                  top: `${pinY(pin())}px`,
                }}
              >
                {session.label}
              </div>
              <WidgetBubble
                x={pinX(pin())}
                y={pinY(pin())}
                setEl={composerPresence.setEl}
                setDragStart={(start) => (composerDragStart = start)}
              >
                <WidgetComposer
                  onDragStart={(e) => composerDragStart?.(e)}
                  onSubmit={(comment) => store.onAddCapture(comment)}
                  onDone={() => store.closeComposerDraft()}
                  onDirty={(dirty) => store.setComposerDirty(dirty)}
                  onDraft={(text) => store.setComposerDraft(text)}
                  initialValue={store.composerDraft()}
                  targets={targets()}
                  onRemoveTarget={(index) => store.removeComposerTarget(index)}
                />
              </WidgetBubble>
            </>
          );
        }}
      </Show>

      {/* keyed: switching pins must not carry uncommitted edits across. */}
      <Show when={bubblePresence.current()} keyed>
        {(entry) => (
          <WidgetBubble
            x={pinX(entry.pin)}
            y={pinY(entry.pin)}
            setEl={bubblePresence.setEl}
            setDragStart={(start) => (editorDragStart = start)}
            onMouseEnter={cancelHoverClose}
            onMouseLeave={() => scheduleHoverClose(entry.id)}
          >
            <WidgetPinEditor
              onDragStart={(e) => editorDragStart?.(e)}
              entry={entry}
              onEditing={() => store.makePinSticky(entry.id)}
              onSave={(comment) => {
                store.updateCollected(entry.id, comment);
                store.closeActivePin();
              }}
              onRemove={() => store.removeCollected(entry.id)}
              onRemoveTarget={(index) => store.removeCollectedTarget(entry.id, index)}
              onAddTarget={() => store.beginLinkingEntry(entry.id)}
              linkingTarget={store.linkingEntryId() === entry.id}
              targetsExpanded={expandedTargetsEntryId() === entry.id}
              onTargetsExpandedChange={(expanded) =>
                setExpandedTargetsEntryId(expanded ? entry.id : null)
              }
              onClose={() => store.closeActivePin()}
            />
          </WidgetBubble>
        )}
      </Show>
    </>
  );
}

type WidgetPinEditorProps = {
  entry: CollectedEntry;
  /** Grip pointerdown from the bubble shell; lets the card be dragged aside. */
  onDragStart?: (e: PointerEvent) => void;
  /** Fired on focus/typing so the bubble pins open until explicitly accepted. */
  onEditing: () => void;
  onSave: (comment: string) => void;
  onRemove: () => void;
  onRemoveTarget: (index: number) => void;
  onAddTarget: () => void;
  linkingTarget: boolean;
  targetsExpanded: boolean;
  onTargetsExpandedChange: (expanded: boolean) => void;
  onClose: () => void;
};

function WidgetPinEditor(props: WidgetPinEditorProps): JSX.Element {
  let textareaRef: HTMLTextAreaElement | undefined;

  onMount(() => {
    if (textareaRef) autosizeTextarea(textareaRef);
  });

  const save = (): void => {
    const value = textareaRef?.value.trim() ?? "";
    if (!value || value === props.entry.item.comment) {
      props.onClose();
      return;
    }
    props.onSave(value);
  };

  return (
    <>
      <button
        type="button"
        data-pl-remove=""
        aria-label="Remove feedback"
        class="border-pl-border bg-pl-background text-pl-muted-foreground hover:text-pl-destructive shadow-pl-circle absolute -top-9 right-0 grid h-8 w-8 cursor-pointer place-items-center rounded-full border hover:bg-[oklch(0.95_0.03_27)]"
        onClick={() => props.onRemove()}
      >
        <WidgetIcon name="trash" size={14} />
      </button>
      <form
        data-pl-pin-editor=""
        class="text-pl-foreground shadow-pl relative rounded-[20px]"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <div class="border-pl-border bg-pl-background relative z-10 overflow-hidden rounded-[20px] border shadow-[0_2px_8px_oklch(0.145_0.018_261/0.08)]">
          <textarea
            ref={textareaRef}
            class={`${CARD_TEXTAREA_CLASS} pr-10`}
            rows={1}
            value={props.entry.item.comment}
            onFocus={() => props.onEditing()}
            onInput={(e) => {
              autosizeTextarea(e.currentTarget);
              props.onEditing();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && !e.isComposing) {
                e.preventDefault();
                save();
              }
            }}
          />
          <div class="flex items-center gap-1 px-2.5 pb-2.5">
            <span class="flex-1" />
            <WidgetBubbleGrip onDragStart={props.onDragStart} />

            <button
              type="submit"
              aria-label="Save comment"
              class="border-pl-primary/80 bg-pl-gradient text-pl-primary-foreground hover:bg-pl-gradient-hover active:bg-pl-gradient-active shadow-pl-circle grid h-8 w-8 shrink-0 cursor-pointer place-items-center rounded-full border transition-transform duration-[130ms] ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.94]"
            >
              <WidgetIcon name="check" size={14} strokeWidth={2.5} />
            </button>
          </div>
        </div>
        <WidgetLinkedTargets
          targets={props.entry.item.linkedElements}
          onRemove={props.onRemoveTarget}
          onAdd={props.onAddTarget}
          adding={props.linkingTarget}
          expanded={props.targetsExpanded}
          onExpandedChange={props.onTargetsExpandedChange}
        />
      </form>
    </>
  );
}
